import React from "react";
import { experience, skillCategories } from "../data/data";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Code2,
  Server,
  Database,
  Cloud,
  Calendar,
  MapPin,
  GraduationCap,
  Briefcase,
} from "lucide-react";

const About = () => {
  const getCategoryIcon = (category) => {
    switch (category) {
      case "Frontend":
        return <Code2 className="w-5 h-5 text-red-500" />;
      case "Backend":
        return <Server className="w-5 h-5 text-red-500" />;
      case "Database":
        return <Database className="w-5 h-5 text-red-500" />;
      case "DevOps & Cloud":
        return <Cloud className="w-5 h-5 text-red-500" />;
      default:
        return <Code2 className="w-5 h-5 text-red-500" />;
    }
  };

  return (
    <section id="about" className="py-20 bg-gray-900">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            About <span className="text-red-600">Me</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Passionate full-stack developer with expertise in modern web
            technologies. I love building scalable applications that solve
            real-world problems.
          </p>
        </div>

        {/* Journey + Experience */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Personal Introduction */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
              <span className="w-1 h-8 bg-red-600 mr-4"></span>
              My Journey
            </h3>
            <div className="space-y-4 text-gray-300 leading-relaxed">
              <p>
                I’m a Software Engineer at Dell Technologies, focused on
                building frontend-heavy, full-stack web systems that scale and
                deliver measurable business impact.
              </p>
              <p>
                Over the past 3.5+ years, I’ve worked across React-based user
                interfaces, backend services in Python, and platform tooling to
                support large-scale design evaluation, customer experience
                analytics, and internal product ecosystems. My work spans
                building production-grade UI platforms, browser extensions, data
                pipelines, and dashboards used across multiple stakeholder
                teams.
              </p>
              <p>
                At Dell, I contributed to the evolution of an end-to-end design
                intelligence workflow, grown from an early proof of concept into
                a multi-component system including a UI scoring platform,
                JavaScript browser extension, remediation workbench, analytics
                dashboards, and large-scale web scraping pipelines. These
                systems process 100+ design metrics across ~4M webpages, improve
                accessibility and usability standards, and support data-driven
                UI decisions at scale. I’ve also worked on modernizing customer
                analytics platforms, consolidating fragmented applications into
                cohesive experiences, improving adoption, and strengthening
                engineering practices through test-driven development, CI/CD
                improvements, and cloud-native deployments.
              </p>
              <p>
                Outside of work, you’ll often find me practicing yoga to reset
                my focus, catching up on a good cricket match, or binge-watching
                documentaries and movies that spark new ideas.
              </p>
            </div>
          </div>

          {/* Experience Timeline */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
              <span className="w-1 h-8 bg-red-600 mr-4"></span>
              Experience & Education
            </h3>

            <div className="relative border-l border-gray-700 pl-6 space-y-8">
              {/* Vertical timeline line */}
              {experience.map((exp, index) => (
                <div key={exp.id} className="relative flex gap-6 items-start">
                  {/* Timeline icon */}
                  <div className="absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full bg-gray-900 border border-red-600 text-red-500">
                    {exp.type === "work" ? (
                      <Briefcase className="w-3.5 h-3.5" />
                    ) : (
                      <GraduationCap className="w-3.5 h-3.5" />
                    )}
                  </div>

                  {/* Experience Card */}
                  <Card className="flex-1 bg-gray-800 border-gray-700 hover:border-red-600 transition-colors duration-300">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="text-lg font-semibold text-white">
                            {exp.position}
                          </h4>
                          <p className="text-red-500 font-medium mb-1">
                            {exp.company} {exp.team ? `| ${exp.team}` : ""}
                          </p>
                          {/* {exp.location && (
                            <div className="flex items-center text-gray-400 text-sm">
                              <MapPin className="w-4 h-4 mr-1" />
                              {exp.location}
                            </div>
                          )} */}
                        </div>
                        <div className="flex items-center text-gray-400 text-sm">
                          <Calendar className="w-4 h-4 mr-1" />
                          {exp.duration}
                        </div>
                      </div>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        {exp.description}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Skills Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
            <span className="w-1 h-8 bg-red-600 mr-4"></span>
            Technical Skills
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {Object.entries(skillCategories).map(([category, skills]) =>
              skills.length > 0 ? (
                <Card
                  key={category}
                  className="bg-gray-800 border-gray-700 hover:border-red-600 transition-colors duration-300"
                >
                  <CardContent className="p-6">
                    <div className="flex items-center mb-4">
                      {getCategoryIcon(category)}
                      <h4 className="text-lg font-semibold text-white ml-3">
                        {category}
                      </h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {skills.map((skill, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="bg-gray-700 text-gray-300 hover:bg-red-600 hover:text-white transition-colors duration-300"
                        >
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ) : null
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
